import { EmptyState } from "@/components/common/EmptyState/EmptyState"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { useArticleList } from "../../../api/articlesApi"
import { useProductList } from "../../../api/productsApi"
import {
  useGlobalSubstanceDetail,
  useGlobalSubstanceList,
  useSubstanceDetail,
  useSubstanceList,
} from "../../../api/substancesApi"
import { useSupplierList } from "../../../api/suppliersApi"
import { singleSelectFacet } from "../../../constants/masterDataFilters"
import { useFacetOptions } from "../../../hooks/useFacetOptions"
import {
  clearUrlParams,
  stringFilterParam,
} from "../../../hooks/useUrlFilterParams"
import { EntityListRowContent } from "../../shared/EntityListRowContent"
import { FilterTabs } from "../../shared/FilterTabs"
import { RelBadge } from "../../shared/RelBadge"
import { EntityListPanel } from "../EntityListPanel"
import type { MasterDataSortValue } from "../MasterDataSort"
import { useRegisterMasterDataAgentContext } from "../masterDataAgentContext"
import { useDefaultRowSelection } from "../useDefaultRowSelection"
import { useNavSeedSearch } from "../useNavSeedSearch"

/** The whole 304-row curated catalog ("catalog") vs. only substances already
 *  linked to one of this org's articles ("org", the default). Mirrored to the
 *  URL so `SubstanceDetailPanel` (a separate component reading the same
 *  browser URL) can independently pick the matching detail-fetch hook. */
export type SubstanceListView = "org" | "catalog"

interface SubstanceListPanelProps {
  /** Opens the standalone create-substance modal (owned by SubstancesPage).
   *  Only rendered as the org-mode empty state's secondary action when
   *  provided — this panel stays decoupled from HOW the modal opens. */
  onAddSubstance?: () => void
}

export function SubstanceListPanel({
  onAddSubstance,
}: SubstanceListPanelProps = {}) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedId = searchParams.get("id")
  useRegisterMasterDataAgentContext("substance", selectedId)

  const [search, setSearch] = useState("")
  const debouncedSearch = useDebouncedValue(search, 300)
  const [sort, setSort] = useState<MasterDataSortValue>("name_asc")

  const [rawView, setRawView] = stringFilterParam(
    searchParams,
    setSearchParams,
    "view",
  )
  const view: SubstanceListView = rawView === "catalog" ? "catalog" : "org"
  const isCatalog = view === "catalog"

  function setView(next: SubstanceListView) {
    // "org" is the default, so drop the param instead of writing it explicitly.
    setRawView(next === "org" ? null : next)
  }

  const [articleFilter, setArticleFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "article_id",
  )
  const [supplierFilter, setSupplierFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "supplier_id",
  )
  const [productFilter, setProductFilter] = stringFilterParam(
    searchParams,
    setSearchParams,
    "product_id",
  )

  // Catalog mode hides these facets entirely (facets={[]} below) — skip the
  // underlying queries too rather than fetching options nobody can select.
  const articleFacet = useFacetOptions(
    useArticleList,
    (a) => ({ value: a.id, label: a.name }),
    !isCatalog,
  )
  const supplierFacet = useFacetOptions(
    useSupplierList,
    (s) => ({ value: s.id, label: s.name }),
    !isCatalog,
  )
  const productFacet = useFacetOptions(
    useProductList,
    (p) => ({ value: p.id, label: p.name }),
    !isCatalog,
  )

  // Org mode: substances already linked to one of the org's articles, with
  // the full facet/sort toolset. Catalog mode: the whole curated registry —
  // it only supports `q`/`limit`, so facets and sort don't apply there.
  const orgQuery = useSubstanceList({
    q: debouncedSearch || undefined,
    article_id: articleFilter ?? undefined,
    supplier_id: supplierFilter ?? undefined,
    product_id: productFilter ?? undefined,
    sort,
    limit: 50,
    enabled: !isCatalog,
  })
  const catalogQuery = useGlobalSubstanceList({
    q: debouncedSearch || undefined,
    limit: 50,
    enabled: isCatalog,
  })
  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading,
  } = isCatalog ? catalogQuery : orgQuery

  const rows = data?.pages.flatMap((p) => p.items) ?? []

  useDefaultRowSelection(rows, selectedId, setSearchParams)

  // The org-scoped read 404s for a substance with no article link yet — the
  // exact case a catalog-mode redirect (e.g. "use existing substance" from
  // the create-conflict panel) always lands on — so the seed fetch must
  // follow `view` the same way the detail panel itself does.
  const isNavRedirect = searchParams.get("source") === "nav"
  const orgSeedDetail = useSubstanceDetail(
    isNavRedirect && !isCatalog ? selectedId : null,
  )
  const catalogSeedDetail = useGlobalSubstanceDetail(
    isNavRedirect && isCatalog ? selectedId : null,
  )
  const seedDetail = isCatalog ? catalogSeedDetail.data : orgSeedDetail.data
  const seedError = isCatalog
    ? catalogSeedDetail.isError
    : orgSeedDetail.isError
  useNavSeedSearch({
    isNavRedirect,
    selectedId,
    detailLoaded: !!seedDetail,
    detailErrored: seedError,
    seedSearchValue: seedDetail
      ? (seedDetail.name ?? seedDetail.casNumber ?? seedDetail.ecNumber ?? "")
      : undefined,
    setSearch,
    setSearchParams,
  })

  function selectRow(substanceId: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("id", substanceId)
      return next
    })
  }

  const viewTabs = (
    <FilterTabs<SubstanceListView>
      options={[
        { value: "org", label: translate("MC_MD_SUBSTANCES_VIEW_ORG") },
        {
          value: "catalog",
          label: translate("MC_MD_SUBSTANCES_VIEW_CATALOG"),
        },
      ]}
      value={view}
      onValueChange={setView}
    />
  )

  const renderContent = (row: (typeof rows)[number]) => (
    <EntityListRowContent
      title={row.name}
      meta={
        <span className="font-mono">
          {row.casNumber ?? row.ecNumber ?? "—"}
        </span>
      }
      badges={
        <RelBadge
          label={translate("MC_MD_REL_LABEL_ARTICLES")}
          labelOne={translate("MC_MD_REL_LABEL_ARTICLES_ONE")}
          count={row.usedInArticlesCount}
          tone="muted"
          items={row.relArticles.map((a) => ({
            id: a.id,
            primary: a.articleNumber,
            secondary: a.name,
          }))}
          itemHref={(id) => `../articles?id=${id}&source=nav`}
          onShowAll={() => navigate(`../articles?substance_id=${row.id}`)}
        />
      }
    />
  )

  // Only the true "nothing here yet" state gets the explanatory empty state —
  // a zero-result search/filter on an org that already has substances must
  // read as "no matches," not "you haven't added anything," so it falls
  // through to the normal (empty) list instead.
  const hasActiveFilter =
    !!debouncedSearch || !!articleFilter || !!supplierFilter || !!productFilter
  if (!isCatalog && !isLoading && rows.length === 0 && !hasActiveFilter) {
    return (
      <div
        data-agent-target="substances.section"
        className="flex h-full flex-col p-6"
      >
        {viewTabs}
        <div className="flex-1 pt-5">
          <EmptyState
            title={translate("MC_MD_SUBSTANCES_EMPTY_TITLE")}
            description={translate("MC_MD_SUBSTANCES_EMPTY_BODY")}
            primaryAction={{
              label: translate("MC_MD_SUBSTANCES_EMPTY_CTA"),
              onClick: () => setView("catalog"),
            }}
            secondaryAction={
              onAddSubstance
                ? {
                    label: translate("MC_MD_PANEL_SUBSTANCES_ADD"),
                    onClick: onAddSubstance,
                  }
                : undefined
            }
          />
        </div>
      </div>
    )
  }

  return (
    <EntityListPanel
      agentTarget="substances.section"
      filterTargetKey="substances.filters"
      tabs={viewTabs}
      rows={rows}
      isLoading={isLoading}
      isFetching={isFetching}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onFetchMore={fetchNextPage}
      selectedId={selectedId}
      onSelectRow={selectRow}
      totalCount={data?.pages[0]?.total}
      sort={isCatalog ? undefined : sort}
      onSortChange={isCatalog ? undefined : setSort}
      search={search}
      onSearchChange={setSearch}
      searchEntityLabelKey="MC_MD_TAB_SUBSTANCES"
      onClearAllFilters={() =>
        clearUrlParams(setSearchParams, [
          "article_id",
          "supplier_id",
          "product_id",
        ])
      }
      facets={
        isCatalog
          ? []
          : [
              {
                key: "article",
                title: translate("MC_MD_FILTER_ARTICLE_TITLE"),
                ...articleFacet,
                ...singleSelectFacet(articleFilter, setArticleFilter),
              },
              {
                key: "supplier",
                title: translate("MC_MD_FILTER_SUPPLIER_TITLE"),
                ...supplierFacet,
                ...singleSelectFacet(supplierFilter, setSupplierFilter),
              },
              {
                key: "product",
                title: translate("MC_MD_FILTER_PRODUCT_TITLE"),
                ...productFacet,
                ...singleSelectFacet(productFilter, setProductFilter),
              },
            ]
      }
      renderContent={renderContent}
    />
  )
}
